const KEY = "marketlink.referralCode";

export function captureReferralCode(code: string | null | undefined) {
  if (typeof window === "undefined") return;
  const ref = String(code || "").trim().toLowerCase();
  if (ref.length < 4) return;
  try {
    window.localStorage.setItem(KEY, ref);
  } catch {
    /* ignore */
  }
}

export function captureReferralFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  captureReferralCode(params.get("ref"));
}

// Recover a referral code that the referee clicked through to /install
// before installing the native app. The server stored (ipHash → code)
// at click time and returns it via /api/public/config when the same IP
// hits the API. Only fills localStorage if no code has already been
// captured locally (URL-based capture wins).
export async function hydrateReferralFromConfig(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(KEY)) return;
    const res = await fetch("/api/public/config", { credentials: "include" });
    if (!res.ok) return;
    const config = (await res.json()) as { pendingReferral?: string | null };
    const code = typeof config.pendingReferral === "string" ? config.pendingReferral.trim().toLowerCase() : "";
    if (code.length >= 4) {
      window.localStorage.setItem(KEY, code);
    }
  } catch {
    /* ignore */
  }
}

export function getStoredReferralCode(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(KEY) || null;
  } catch {
    return null;
  }
}

// Called once after auth completes (regardless of method — email
// signup, Google OAuth, or returning sign-in). Sends the localStorage
// referral code to the server, which applies it idempotently. Needed
// because the Google OAuth callback path doesn't have access to the
// client-side localStorage at signup time, so OAuth-signed-up users
// would otherwise never get attributed even though they clicked a
// referral link before signing up.
export async function applyStoredReferralCodeAfterAuth(): Promise<void> {
  if (typeof window === "undefined") return;
  const code = getStoredReferralCode();
  if (!code) return;
  try {
    const res = await fetch("/api/auth/apply-referral", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ referralCode: code }),
    });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      // Always clear after a successful response — either the code was
      // applied or the user was already attributed; either way the
      // localStorage value has served its purpose.
      if (data?.ok) clearStoredReferralCode();
    }
  } catch {
    /* network blip is fine — we'll retry next auth cycle */
  }
}

export function clearStoredReferralCode() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
